import { useCallback, useEffect, useRef, useState } from "react";
import { TextInput } from "@mantine/core";
import {
  getFragmentCountDisplayValue,
  normalizeFragmentChipValue,
  type FragmentCountPatch,
} from "../../utils/fragmentCount";

const normalizeDirectFragmentCountValue = (
  value: string
): FragmentCountPatch | null => normalizeFragmentChipValue(value, null, null);

interface FragmentCountInputProps {
  canonicalValue: string;
  disabled: boolean;
  onFragmentCountChange: (patch: FragmentCountPatch) => void;
}

export default function FragmentCountInput({
  canonicalValue,
  disabled,
  onFragmentCountChange,
}: FragmentCountInputProps) {
  const [draft, setDraft] = useState(canonicalValue);
  const committedValueRef = useRef(canonicalValue);
  const lastCanonicalValueRef = useRef(canonicalValue);
  const isFocusedRef = useRef(false);
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (canonicalValue === lastCanonicalValueRef.current) return;
    if (isFocusedRef.current && isEditingRef.current) return;

    lastCanonicalValueRef.current = canonicalValue;
    committedValueRef.current = canonicalValue;
    setDraft(canonicalValue);
  }, [canonicalValue]);

  const commitPatch = useCallback(
    (patch: FragmentCountPatch) => {
      const committedValue = getFragmentCountDisplayValue(
        patch.fragmentCount,
        patch.fragmentCountQualifier
      );
      setDraft(committedValue);
      if (committedValue === committedValueRef.current) return;
      committedValueRef.current = committedValue;
      onFragmentCountChange(patch);
    },
    [onFragmentCountChange]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.currentTarget.value;
      isEditingRef.current = true;
      setDraft(rawValue);
      if (!rawValue.trim()) return;

      const patch = normalizeDirectFragmentCountValue(rawValue);
      if (!patch) return;
      commitPatch(patch);
    },
    [commitPatch]
  );

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    isEditingRef.current = false;
    lastCanonicalValueRef.current = canonicalValue;
    const patch = normalizeDirectFragmentCountValue(draft);
    if (!patch) {
      committedValueRef.current = canonicalValue;
      setDraft(canonicalValue);
      return;
    }
    commitPatch(patch);
  }, [canonicalValue, commitPatch, draft]);

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  return (
    <TextInput
      label="Fragment Count"
      placeholder="# of fragments or Multiple"
      value={draft}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      inputMode="text"
      disabled={disabled}
      tabIndex={-1}
      styles={{
        input: { height: 36 },
      }}
    />
  );
}
